import { useCallback, useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onFetchNextPage: () => void;
    /** Distance in pixels from viewport bottom at which to trigger fetch. */
    rootMargin?: string;
}

/**
 * Wire an IntersectionObserver to a sentinel ref. When the sentinel becomes
 * visible (or near-visible) the callback fires — but only if there's more
 * data and we're not already loading. Reusable across any infinite list.
 */
export function useInfiniteScroll(
    options: UseInfiniteScrollOptions,
): (node: Element | null) => void {
    const { hasNextPage, isFetchingNextPage, onFetchNextPage, rootMargin = '200px' } = options;
    const observerRef = useRef<IntersectionObserver | null>(null);

    // We keep the latest callback in a ref so the IntersectionObserver
    // doesn't have to re-subscribe on every render.
    const callbackRef = useRef(onFetchNextPage);
    useEffect(() => {
        callbackRef.current = onFetchNextPage;
    }, [onFetchNextPage]);

    return useCallback(
        (node: Element | null) => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            if (!node || !hasNextPage || isFetchingNextPage) {
                return;
            }
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    const entry = entries[0];
                    if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
                        callbackRef.current();
                    }
                },
                { rootMargin },
            );
            observerRef.current.observe(node);
        },
        [hasNextPage, isFetchingNextPage, rootMargin],
    );
}
